import React, { useCallback, useEffect, useState } from 'react';
import { WithTranslateProps } from '../../WithTranslate';
import { MultiCalendarSelectionType } from '../MultiCalendar';
import { Button, Card, Label, Position, Tooltip } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { IconNames } from '@blueprintjs/icons';
import {
  DivLine,
  DivLineWithButtonMargin,
  NavigateContainer,
  imageButtonStyle,
} from '../../styledComponents/StyledComponents';
import { ToolboxToolParts } from './ToolboxParts';
import { TLCode } from '../../../../common/enums';
import { TranslatedMessage } from '../../translation';
import {
  bankHoliday,
  bankHolidayDutyOffRequest,
  dutyDay,
  dutyDayNight,
  dutyNight,
  iconModification,
  iconMultipleActivities,
  iconSelectedDateForCandidates,
  iconToday,
} from '../../../../assets';
import { YearNavigation } from './YearNavigation';
import NotificationToast from '../../NotificationToast';
import { SemesterInfo } from './SemesterInfo';

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 20px;
  line-height: 27spx;
`;

const Title = styled.h2({
  marginRight: '120px',
  marginTop: 0,
  marginBottom: 2,
});

interface ToolboxAndLegendProps extends WithTranslateProps {
  blackPointHolidayCount?: number;
  blackPointHolidayMax?: number;
  isSecretariat?: boolean;
  navigate: (data: any) => void;
  fly?: number;
  firstBPEncodingDate: string;
  onTypeChange: (type: MultiCalendarSelectionType | undefined) => void;
  selectedType?: MultiCalendarSelectionType | undefined;
  onS1Clicked?: () => void;
  onS2Clicked?: () => void;
}

export const ToolboxAndLegend = ({
  blackPointHolidayCount,
  blackPointHolidayMax,
  isSecretariat,
  navigate,
  fly,
  firstBPEncodingDate,
  onTypeChange,
  selectedType,
  onS1Clicked,
  onS2Clicked,
  translate,
}: ToolboxAndLegendProps) => {
  const [typeSelection, setTypeSelection] = useState(selectedType);

  const onTypeClicked = useCallback(
    (type: MultiCalendarSelectionType | undefined) => {
      if (type !== typeSelection) {
        setTypeSelection(type);
        onTypeChange(type);
      }
    },
    [typeSelection, setTypeSelection, onTypeChange],
  );

  useEffect(() => {
    if (
      blackPointHolidayCount != null &&
      blackPointHolidayMax != null &&
      (blackPointHolidayMax - blackPointHolidayCount <= 0 && typeSelection === MultiCalendarSelectionType.blackpoint)
    ) {
      NotificationToast.showInfo(translate(TLCode.MULTI_CALENDAR_MsgYouHaveReachedQuota));
      onTypeClicked(undefined);
    }
    if (selectedType !== typeSelection) setTypeSelection(typeSelection);
  }, [typeSelection, setTypeSelection, onTypeClicked, selectedType, blackPointHolidayCount, blackPointHolidayMax]);

  return (
    <Card
      style={{
        margin: 5,
        padding: 5,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
      }}
    >
      <HorizontalContainer>
        <DivLine>
          <DivColumn>
            <DivLineWithButtonMargin>
              <ToolboxToolParts
                translate={translate}
                selectedType={typeSelection}
                onTypeClicked={onTypeClicked}
                isSecretariat={isSecretariat}
                blackPointHolidayCount={blackPointHolidayCount}
                blackPointHolidayMax={blackPointHolidayMax}
              />
              <DivColumn>
                <DivLineWithButtonMargin>
                  <Title>
                    <TranslatedMessage id={TLCode.MULTI_CALENDAR_Legend} />
                  </Title>
                </DivLineWithButtonMargin>
                <DivLineWithButtonMargin>
                  {!isSecretariat && (
                    <>
                      <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDay)} position={Position.BOTTOM}>
                        <Button className={imageButtonStyle.toString()} disabled={true}>
                          <img src={dutyDay} alt="dutyDay" />
                        </Button>
                      </Tooltip>
                      <Tooltip
                        content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDayNight)}
                        position={Position.BOTTOM}
                      >
                        <Button className={imageButtonStyle.toString()} disabled={true}>
                          <img src={dutyDayNight} alt="dutyDayNight" />
                        </Button>
                      </Tooltip>
                      <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyNight)} position={Position.BOTTOM}>
                        <Button className={imageButtonStyle.toString()} disabled={true}>
                          <img src={dutyNight} alt="dutyNight" />
                        </Button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyNight)} position={Position.BOTTOM}>
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={dutyNight} alt="dutyNight" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BankHoliday)} position={Position.BOTTOM}>
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={bankHoliday} alt="bankHoliday" />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BankHolidayDutyOffRequest)}
                    position={Position.BOTTOM}
                  >
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={bankHolidayDutyOffRequest} alt="bankHolidayDutyOffRequest" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_Modification)} position={Position.BOTTOM}>
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={iconModification} alt="iconModification" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_Today)} position={Position.BOTTOM}>
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={iconToday} alt="iconToday" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={translate(TLCode.DUTIES_Date_Conflicts_Title)} position={Position.BOTTOM}>
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={iconMultipleActivities} alt="iconMultipleActivities" />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    content={translate(TLCode.DUTIES_Date_Selected_For_Candidates_Title)}
                    position={Position.BOTTOM}
                  >
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <img src={iconSelectedDateForCandidates} alt="iconSelectedDateForCandidates" />
                    </Button>
                  </Tooltip>
                  <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_ExchangeDuty)} position={Position.BOTTOM}>
                    <Button className={imageButtonStyle.toString()} disabled={true}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10" width="16" height="16" fill="none" style={{ display: 'block' }}>
                        <path d="M1 3h7" stroke="#ff0080" strokeWidth="1.5" strokeLinecap="round" />
                        <path
                          d="M6 1l2 2-2 2"
                          stroke="#ff0080"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path d="M9 7H2" stroke="#ff0080" strokeWidth="1.5" strokeLinecap="round" />
                        <path
                          d="M4 5l-2 2 2 2"
                          stroke="#ff0080"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Button>
                  </Tooltip>
                </DivLineWithButtonMargin>{' '}
              </DivColumn>
            </DivLineWithButtonMargin>
            <DivLineWithButtonMargin>
              <Label>
                <b>
                  <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_EncodingIsPossible} />
                  &nbsp;<u>{firstBPEncodingDate}</u>
                </b>
              </Label>
            </DivLineWithButtonMargin>
          </DivColumn>
        </DivLine>
        <DivLine>
          <SemesterInfo year={fly} translate={translate} />
        </DivLine>
        <DivLine>
          <NavigateContainer>
            <YearNavigation
              translate={translate}
              previousYear={() => navigate('UP')}
              nextYear={() => navigate('DOWN')}
              yearSelected={fly}
              onS1Clicked={onS1Clicked}
              onS2Clicked={onS2Clicked}
            />
          </NavigateContainer>
        </DivLine>
      </HorizontalContainer>
    </Card>
  );
};
